import { useCallback, useMemo } from 'react';

import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount } from '@feathr/extender/state';
import type { ITransport } from '@feathr/hooks';
import { MockTransport, SSETransport } from '@feathr/hooks';

/**
 * Creates an SSE transport for realtime donation events.
 *
 * In non-production environments with a valid account, returns an SSETransport
 * connected to the donations stream endpoint. In production or without an account,
 * returns a MockTransport that does nothing.
 *
 * Note: This hook requires account context. Only call it after the account is loaded.
 *
 * @returns ITransport instance for use with RealtimeProvider
 */
function useDonationRealtimeTransport(): ITransport {
  const account = useAccount();
  const { isOnProduction } = useBlackboxVersion();
  const accountId = account.id;

  const getSSEUrl = useCallback((): string => {
    const baseUrl = BLACKBOX_URL.replace(/\/$/, '');
    return `${baseUrl}/stream/${accountId}/donations/`;
  }, [accountId]);

  const transport = useMemo((): ITransport => {
    if (!isOnProduction && accountId) {
      return new SSETransport(getSSEUrl, {
        defaultEventType: 'fundraising.donation.created',
      });
    }
    return new MockTransport();
  }, [isOnProduction, accountId, getSSEUrl]);

  return transport;
}

export default useDonationRealtimeTransport;
